import React, {useCallback, useState} from 'react';
import {HierarchyTreeNode} from 'src/js/model/hierarchy_tree_children';

import {HierarchySyncNode} from './HierarchySyncNode';

interface OwnProps {
  hierarchyId: string | number;
  isTogglingSeparated?: boolean;
  level: number;
  node: HierarchyTreeNode;
  onNodeClick?: (node: HierarchyTreeNode) => void;
  showRoot?: boolean;
  tabIndex?: number;
  highlightedValues?: string[];
  getOptionDisabled?: (row: any) => boolean;
}

type Props = OwnProps;

export const HierarchySyncNodeContainer: React.FC<Props> = (props) => {
  const {
    hierarchyId,
    isTogglingSeparated = false,
    level = 0,
    node,
    onNodeClick,
    showRoot = true,
    tabIndex = 0,
    highlightedValues,
    getOptionDisabled,
  } = props;

  const [isOpened, setIsOpened] = useState<boolean>(false);

  const handleInnerOrLeafNodeClick = useCallback(
    (node: HierarchyTreeNode) => {
      if (
        node &&
        onNodeClick &&
        (isTogglingSeparated ||
          !node.hasChildren() ||
          (node.hasChildren() && !isOpened))
      ) {
        onNodeClick(node);
      }
    },
    [onNodeClick, isTogglingSeparated, isOpened]
  );

  const toggleOpened = useCallback(() => {
    setIsOpened((prevState) => !prevState);
  }, []);

  const toggleInnerNode = useCallback(
    (node: HierarchyTreeNode) => {
      if (node && node.hasChildren()) {
        toggleOpened();
      }
    },
    [toggleOpened]
  );

  const toggleNodeAndHandleClick = useCallback(
    (node: HierarchyTreeNode) => {
      toggleInnerNode(node);
      handleInnerOrLeafNodeClick(node);
    },
    [toggleInnerNode, handleInnerOrLeafNodeClick]
  );

  const handleTickClick = useCallback(
    (node: HierarchyTreeNode) => {
      if (isTogglingSeparated) {
        toggleInnerNode(node);
      } else {
        toggleNodeAndHandleClick(node);
      }
    },
    [isTogglingSeparated, toggleInnerNode, toggleNodeAndHandleClick]
  );

  const handleNodeClick = useCallback(
    (node: HierarchyTreeNode) => {
      if (isTogglingSeparated) {
        handleInnerOrLeafNodeClick(node);
      } else {
        toggleNodeAndHandleClick(node);
      }
    },
    [isTogglingSeparated, handleInnerOrLeafNodeClick, toggleNodeAndHandleClick]
  );

  return (
    <HierarchySyncNode
      key={node.getId()}
      hierarchyId={hierarchyId}
      hierarchyChildren={node.getChildren()}
      isOpened={isOpened}
      isTogglingSeparated={isTogglingSeparated}
      level={level}
      node={node}
      onNodeClick={handleNodeClick}
      showRoot={showRoot}
      srcNodeClick={onNodeClick}
      tabIndex={tabIndex}
      toggleOpened={handleTickClick}
      highlightedValues={highlightedValues}
      getOptionDisabled={getOptionDisabled}
    />
  );
};

HierarchySyncNodeContainer.displayName = 'HierarchyNodeContainer';
